import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, defaultRouteFor, hasModuleAccess } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import MasterStock from "@/pages/MasterStock";
import Housekeeping from "@/pages/Housekeeping";
import Rooms from "@/pages/Rooms";
import Laundry from "@/pages/Laundry";
import Store from "@/pages/Store";
import Checkout from "@/pages/Checkout";
import MissingDamaged from "@/pages/MissingDamaged";
import Discrepancies from "@/pages/Discrepancies";
import Purchases from "@/pages/Purchases";
import Consumption from "@/pages/Consumption";
import Closing from "@/pages/Closing";
import AdminSettings from "@/pages/AdminSettings";
import Audit from "@/pages/Audit";
import AssetMaster from "@/pages/AssetMaster";
import AssetRooms from "@/pages/AssetRooms";
import AssetTransfers from "@/pages/AssetTransfers";
import AssetMaintenance from "@/pages/AssetMaintenance";
import AssetWarranty from "@/pages/AssetWarranty";
import AssetReports from "@/pages/AssetReports";
import AssetSettings from "@/pages/AssetSettings";
import AssetUnitDetail from "@/pages/AssetUnitDetail";

// Access matrix
const ROLES_FULL_ADMIN_VIEW = ["admin", "accounts", "hk_supervisor"]; // Dashboard, Master Stock, Asset Management full read
const ROLES_DASHBOARD = ["admin", "accounts", "hk_supervisor", "front_office"];
const ROLES_ASSET_OPERATIONAL = [
  "admin",
  "accounts",
  "hk_supervisor",
  "hk_staff",
  "laundry",
  "front_office",
];
const ROLES_MAINT_SCOPE = ["admin", "accounts", "hk_supervisor", "maintenance"]; // Maintenance & Warranty screens
const ROLES_HK_OPS = ["admin", "accounts", "hk_supervisor", "hk_staff"];
const ROLES_ROOM_PAR_EDIT = ["admin", "hk_supervisor", "front_office"]; // Front Office keeps room-specific par
const ROLES_PURCHASES = ["admin", "accounts", "hk_supervisor"];
const ROLES_CONSUMPTION_VIEW = ["admin", "accounts", "hk_supervisor"];

function Protected({ children, roles, module, minimum = "view" }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">
        Loading Varuna Inn…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force password change on first login before anything else.
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to={defaultRouteFor(user.role)} replace />;
  if (module && !hasModuleAccess(user, module, minimum)) {
    return <Navigate to={defaultRouteFor(user.role)} replace />;
  }
  return children;
}

function RootLanding() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultRouteFor(user.role)} replace />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootLanding />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
            <Route path="/dashboard" element={<Protected module="dashboard"><Dashboard /></Protected>} />
            <Route path="/master-stock" element={<Protected module="master_linen_stock"><MasterStock /></Protected>} />
            <Route path="/housekeeping" element={<Protected roles={[...ROLES_HK_OPS, "front_office"]}><Housekeeping /></Protected>} />
            <Route path="/rooms" element={<Protected><Rooms /></Protected>} />
            <Route path="/laundry" element={<Protected roles={["admin","accounts","hk_supervisor","hk_staff","laundry","front_office"]}><Laundry /></Protected>} />
            <Route path="/store" element={<Protected module="store_inventory"><Store /></Protected>} />
            <Route path="/checkout" element={<Protected roles={["admin","accounts","hk_supervisor","hk_staff","front_office"]}><Checkout /></Protected>} />
            <Route path="/missing-damaged" element={<Protected roles={["admin","accounts","hk_supervisor","hk_staff","laundry","front_office"]}><MissingDamaged /></Protected>} />
            <Route path="/discrepancies" element={<Protected roles={["admin","accounts","hk_supervisor"]}><Discrepancies /></Protected>} />
            <Route path="/purchases" element={<Protected module="purchase_register"><Purchases /></Protected>} />
            <Route path="/consumption" element={<Protected roles={ROLES_CONSUMPTION_VIEW}><Consumption /></Protected>} />
            <Route path="/closing" element={<Protected roles={["admin","accounts","hk_supervisor"]}><Closing /></Protected>} />
            <Route path="/admin" element={<Protected roles={ROLES_FULL_ADMIN_VIEW}><AdminSettings /></Protected>} />
            {/* Asset Management module — Admin/Accounts/Supervisor for full module; Maintenance role only for maintenance & warranty screens */}
            <Route path="/assets" element={<Protected module="asset_management"><Navigate to="/dashboard?tab=assets" replace /></Protected>} />
            <Route path="/assets/master" element={<Protected roles={ROLES_ASSET_OPERATIONAL} module="asset_management"><AssetMaster /></Protected>} />
            <Route path="/assets/rooms" element={<Protected roles={ROLES_ASSET_OPERATIONAL} module="asset_management"><AssetRooms /></Protected>} />
            <Route path="/assets/store" element={<Protected module="asset_management"><Navigate to="/store?tab=assets" replace /></Protected>} />
            <Route path="/assets/purchases" element={<Protected module="asset_management"><Navigate to="/purchases?tab=assets" replace /></Protected>} />
            <Route path="/assets/incidents" element={<Protected module="asset_management"><Navigate to="/missing-damaged?tab=assets" replace /></Protected>} />
            <Route path="/assets/transfers" element={<Protected roles={ROLES_ASSET_OPERATIONAL} module="asset_management"><AssetTransfers /></Protected>} />
            <Route path="/assets/maintenance" element={<Protected roles={ROLES_MAINT_SCOPE} module="asset_management"><AssetMaintenance /></Protected>} />
            <Route path="/assets/warranty" element={<Protected roles={ROLES_MAINT_SCOPE} module="asset_management"><AssetWarranty /></Protected>} />
            <Route path="/assets/reports" element={<Protected roles={ROLES_ASSET_OPERATIONAL} module="asset_management"><AssetReports /></Protected>} />
            <Route path="/assets/settings" element={<Protected roles={ROLES_FULL_ADMIN_VIEW} module="asset_management"><AssetSettings /></Protected>} />
            <Route path="/assets/scan/:assetId" element={<Protected roles={ROLES_MAINT_SCOPE} module="asset_management"><AssetUnitDetail /></Protected>} />
            <Route path="/audit" element={<Protected module="audit_log"><Audit /></Protected>} />
            <Route path="*" element={<RootLanding />} />
          </Routes>
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
